import { useCallback } from "react";

import { getEventCoordinates } from "../lib";

type UseDragHandlerOptions = {
  elementRef: React.RefObject<HTMLDivElement | null>;
  onUpdate: (rect: DOMRect, coords: { clientX: number; clientY: number }) => void;
  onEnd?: (rect: DOMRect, coords: { clientX: number; clientY: number }) => void;
};

export const useDragHandler = ({ elementRef, onUpdate, onEnd }: UseDragHandlerOptions) => {
  const handleStart = useCallback(
    (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
      if (!elementRef.current) return;

      const rect = elementRef.current.getBoundingClientRect();
      const coords = getEventCoordinates(e);
      onUpdate(rect, coords);
    },
    [elementRef, onUpdate]
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      handleStart(e);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        if (!elementRef.current) return;
        const rect = elementRef.current.getBoundingClientRect();
        const coords = getEventCoordinates(moveEvent);
        onUpdate(rect, coords);
      };

      const handleMouseUp = () => {
        if (onEnd && elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const coords = getEventCoordinates(new MouseEvent("mouseup", { clientX: 0, clientY: 0 }));
          onEnd(rect, coords);
        }
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [handleStart, elementRef, onUpdate, onEnd]
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent<HTMLDivElement>) => {
      handleStart(e);

      const handleTouchMove = (moveEvent: TouchEvent) => {
        if (!elementRef.current) return;
        const rect = elementRef.current.getBoundingClientRect();
        const coords = getEventCoordinates(moveEvent);
        onUpdate(rect, coords);
      };

      const handleTouchEnd = () => {
        if (onEnd && elementRef.current) {
          const rect = elementRef.current.getBoundingClientRect();
          const coords = getEventCoordinates(new TouchEvent("touchend"));
          onEnd(rect, coords);
        }
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };

      document.addEventListener("touchmove", handleTouchMove, { passive: false });
      document.addEventListener("touchend", handleTouchEnd);
    },
    [handleStart, elementRef, onUpdate, onEnd]
  );

  return { handleMouseDown, handleTouchStart };
};
